import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import store, { AllState } from '../../../../store'
import {
  changeGanttParameter,
  RootGanttParameterLoad,
} from '../../../../store/project'
import {
  FitPeriod,
  GanttParameterVO,
} from '../../../../domain/value-object/GanttParameterVO'
import { useProjectPrivateContext } from '../../../context/projectContext'
import { getFinancialStartMonth } from '../../../../utils/date'
import DateVO from '../../../../vo/DateVO'
import { ProjectPlanNewRow } from '../projectPlanNew'

const PROJECT_ROOT_FIT_TOKEN = 'PROJECT_ROOT'

type PendingFitToken = RootGanttParameterLoad | typeof PROJECT_ROOT_FIT_TOKEN

export const useGanttFitPeriod = (
  rootProjectPlanUuid: string | undefined,
  rootProjectPlan: ProjectPlanNewRow | undefined
): FitPeriod | undefined => {
  const { project } = useProjectPrivateContext()
  return useMemo(() => {
    if (!rootProjectPlanUuid) {
      return GanttParameterVO.findFitPeriod([
        {
          start: project.scheduledDate.startDate,
          end: project.scheduledDate.endDate,
        },
      ])
    }
    const cumulation = rootProjectPlan?.body?.cumulation
    const scheduledDate = rootProjectPlan?.body?.wbsItem?.scheduledDate
    return GanttParameterVO.findFitPeriod([
      {
        start: cumulation?.minScheduledStartDate,
        end: cumulation?.maxScheduledEndDate,
      },
      { start: scheduledDate?.startDate, end: scheduledDate?.endDate },
    ])
  }, [rootProjectPlanUuid, rootProjectPlan, project.scheduledDate])
}

const resolvePendingFitToken = (
  rootProjectPlanUuid: string | undefined,
  isProjectSaved: boolean | undefined,
  rootLoad: RootGanttParameterLoad | undefined
): PendingFitToken | undefined => {
  if (!rootProjectPlanUuid) {
    return isProjectSaved === false ? PROJECT_ROOT_FIT_TOKEN : undefined
  }
  const isUnsavedRoot =
    rootLoad?.rootProjectPlanUuid === rootProjectPlanUuid && !rootLoad.isSaved
  return isUnsavedRoot ? rootLoad : undefined
}

export const useInitialGanttFit = ({
  projectUuid,
  rootProjectPlanUuid,
  isRootRowLoaded,
  fitPeriod,
}: {
  projectUuid: string
  rootProjectPlanUuid: string | undefined
  isRootRowLoaded: boolean
  fitPeriod: FitPeriod | undefined
}) => {
  const isProjectSaved = useSelector(
    (state: AllState) => state.project.isProjectGanttParameterSaved
  )
  const rootLoad = useSelector(
    (state: AllState) => state.project.rootGanttParameterLoad
  )
  const fiscalStartMonth = useSelector((state: AllState) =>
    getFinancialStartMonth(state.tenant.organization?.financialYearEndMonth)
  )
  const handledTokenRef = useRef<PendingFitToken>()

  const pendingToken = resolvePendingFitToken(
    rootProjectPlanUuid,
    isProjectSaved,
    rootLoad
  )
  const isReady = isProjectSaved !== undefined && isRootRowLoaded

  useEffect(() => {
    if (!pendingToken || !isReady) return
    if (handledTokenRef.current === pendingToken) return
    handledTokenRef.current = pendingToken
    if (!fitPeriod) return
    const current =
      store.getState().project.ganttParameter ?? GanttParameterVO.defaultValue()
    const fitted = GanttParameterVO.fitRangeToPeriod(
      new DateVO(fitPeriod.start),
      new DateVO(fitPeriod.end),
      current.startDay,
      fiscalStartMonth
    )
    store.dispatch(
      changeGanttParameter(
        projectUuid,
        new GanttParameterVO(
          fitted.unit,
          current.startDay,
          fitted.range,
          current.displayOptions
        ),
        rootProjectPlanUuid
      )
    )
  }, [
    pendingToken,
    isReady,
    fitPeriod,
    fiscalStartMonth,
    projectUuid,
    rootProjectPlanUuid,
  ])
}
